import { ZfBox, ZfLoadingSpinner } from '@zafire/react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import moment from 'moment';
import type { FunctionComponent } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCashFlow, useClient, useEvents, useProjection, useProjectionCashEventSettings } from '../../../api';
import type { ChartType } from '../../../config/constants';
import { MONTH_12_VALUE } from '../../../config/constants';
import { useChartBaseCustomizationCallbacks } from '../../../hooks/chart/useChartBaseCustomizationCallbacks';
import { useChartFormatter } from '../../../hooks/chart/useChartFormatter';
import { useChartNavigatorCallbacks } from '../../../hooks/chart/useChartNavigatorCallbacks';
import { useChartRefreshAfterMount } from '../../../hooks/chart/useChartRefreshAfterMount';
import { useYearEventMap } from '../../../hooks/chart/useYearEventMap';
import type { ITooltipContent } from '../../../interfaces/chart';
import type { RoadmapEvent } from '../../../interfaces/event';
import type { ExtendedSeriesOptionType, HighchartCallbackFn, IOnChartLoadCallbackArgs } from '../../../interfaces/projection';
import { isMaturity } from '../../../utils/eventUtils';
import { NeedEventModal } from '../../topic/modals/NeedEventModal';
import { CashEventList } from '../cashEventPane/CashEventList';
import { CashEventPane } from '../cashEventPane/CashEventPane';
import { getChartOptions } from './chartOptions';

interface IProps {
    chartType: ChartType;
    projectionPeriodYears: number;
    seriesData: Highcharts.SeriesOptionsType[] | ExtendedSeriesOptionType[];
    crosshairXaxis?: boolean;
    isDrawerOpen: boolean;
    onChartLoadEventHandlers?: HighchartCallbackFn<Highcharts.Chart>[];
    onChartRenderEventHandlers?: HighchartCallbackFn<Highcharts.Chart>[];
    setChart?: (chart: Highcharts.Chart) => void;
}

export const ChartWrapper: FunctionComponent<IProps> = ({
    chartType,
    projectionPeriodYears,
    seriesData,
    crosshairXaxis,
    isDrawerOpen,
    onChartLoadEventHandlers,
    onChartRenderEventHandlers,
    setChart,
}) => {
    const { t, i18n } = useTranslation();
    const xAxisYPosition = useRef(undefined);
    const { isSuccess: isSaeProjectionSuccess, isLoading: isSaeProjectionLoading } = useProjection();
    const { isSuccess: isCashFlowProjectionSuccess, isLoading: isCashFlowProjectionLoading } = useCashFlow();
    const { data: cashEventSettings } = useProjectionCashEventSettings();

    const isSuccess = (chartType === 'MONTE_CARLO_PROJECTION' && isSaeProjectionSuccess) || (chartType === 'CASH_FLOW' && isCashFlowProjectionSuccess);
    const isLoading = (chartType === 'MONTE_CARLO_PROJECTION' && isSaeProjectionLoading) || (chartType === 'CASH_FLOW' && isCashFlowProjectionLoading);

    const { data: client } = useClient();
    const {
        data: { lifeEvents, maturities },
    } = useEvents();

    const cashFlowRelevantEvents = () => {
        if (cashEventSettings?.enabled) {
            return [...maturities, ...lifeEvents].filter((event) => {
                return cashEventSettings.itemsSettings.some(
                    (itemSetting) => itemSetting.configurableKey === event.configurableKey && itemSetting.enabled && !itemSetting.hidden,
                );
            });
        }

        return [];
    };

    const { tooltipFormatter, xAxisLabelsFormatter } = useChartFormatter({
        chartType,
        clientBirthdate: client?.birthdate,
        series: seriesData as ExtendedSeriesOptionType[],
        allEvents: cashFlowRelevantEvents(),
        projectionPeriodYears,
    });

    const customizeChartBase = useChartBaseCustomizationCallbacks({ clientBirthdate: client?.birthdate });

    const { yearEventMap, updateEventCoordinates } = useYearEventMap({
        xAxisYPosition,
        projectionPeriodYears,
        allEvents: cashFlowRelevantEvents(),
    });

    const { setExtremes, setMinMaxNavigatorTimeStamps, setMaxWidthOfNavigator, setChartInstance } = useChartNavigatorCallbacks({
        updateEventCoordinates,
        setChart,
    });
    const [tooltipContent, setTooltipContent] = useState<ITooltipContent | undefined>(undefined);
    const [editedEvent, setEditedEvent] = useState<RoadmapEvent | undefined>(undefined);

    // Re-lays out the x-axis labels once after mount so they don't overlap until a drawer toggle.
    const { refreshKey, onChartCreated } = useChartRefreshAfterMount();

    const getYearEntry = () => {
        if (!tooltipContent) {
            return undefined;
        }
        const year = tooltipContent.time.year();
        if (tooltipContent.time.month() === MONTH_12_VALUE) {
            const nextYear = year + 1;

            return yearEventMap && nextYear in yearEventMap ? yearEventMap[nextYear] : undefined;
        }

        return yearEventMap && year in yearEventMap ? yearEventMap[year] : undefined;
    };

    const yearEntry = getYearEntry();

    const handleCloseEventList = () => setTooltipContent(undefined);

    const getChartRenderEventHandlers = () => {
        let chartRenderEventHandlers: HighchartCallbackFn<Highcharts.Chart>[] = [];
        if (onChartRenderEventHandlers) {
            chartRenderEventHandlers = [...onChartRenderEventHandlers];
        }
        chartRenderEventHandlers.push(setExtremes);
        chartRenderEventHandlers.push(customizeChartBase);

        return chartRenderEventHandlers;
    };

    const getChartLoadEventHandlers = () => {
        let chartLoadEventHandlers: HighchartCallbackFn<IOnChartLoadCallbackArgs | Highcharts.Chart>[] = [];
        if (onChartLoadEventHandlers) {
            // @ts-expect-error Problems with missing Highcharts types
            chartLoadEventHandlers = [...onChartLoadEventHandlers];
        }
        // @ts-expect-error Problems with missing Highcharts types
        chartLoadEventHandlers.push(setMinMaxNavigatorTimeStamps);
        // @ts-expect-error Problems with missing Highcharts types
        chartLoadEventHandlers.push(setMaxWidthOfNavigator);
        // @ts-expect-error Problems with missing Highcharts types
        chartLoadEventHandlers.push(setChartInstance);

        return chartLoadEventHandlers;
    };

    const pointClickHandler = (tooltipContent: ITooltipContent) => {
        if (chartType === 'CASH_FLOW') {
            tooltipContent = { ...tooltipContent, title: t('cashflowAsOfYear', { year: tooltipContent.time.year() }) };
        } else if (chartType === 'MONTE_CARLO_PROJECTION') {
            tooltipContent = {
                ...tooltipContent,
                title: t('expectedAssets', { time: moment(tooltipContent?.title).format('MM.YYYY') }),
            };
        }
        setTooltipContent(tooltipContent);
    };

    const chart = useMemo(
        () => {
            if (!isSuccess) return null;

            const options = getChartOptions({
                onChartLoadEventHandlers: getChartLoadEventHandlers(),
                onChartRenderEventHandlers: getChartRenderEventHandlers(),
                xAxisLabelsFormatterFn: xAxisLabelsFormatter,
                tooltipFormatterFn: tooltipFormatter,
                onClickPointHandler: pointClickHandler,
                translation: i18n,
                yearRange: {
                    from: moment().year(),
                    to: moment().year() + projectionPeriodYears,
                },
                // @ts-expect-error Problems with missing Highcharts types
                refs: { xAxisYPosition },
                seriesData,
                clientBirthdate: client?.birthdate,
                crosshairXaxis,
                projectionPeriod: projectionPeriodYears,
            });

            return (
                <ZfBox
                    style={{
                        flexGrow: 1,
                        marginRight: 0,
                        transition: isDrawerOpen ? 'margin 195ms cubic-bezier(0.0, 0, 0.2, 1)' : 'margin 225ms cubic-bezier(0.4, 0, 0.6, 1)',
                        maxWidth: '100%',
                    }}
                >
                    {' '}
                    <HighchartsReact
                        id="container"
                        containerProps={{ className: 'chart-container__full-height' }}
                        highcharts={Highcharts}
                        constructorType="chart"
                        options={options}
                        callback={onChartCreated}
                    />
                </ZfBox>
            );
        }, // eslint-disable-next-line
        [seriesData, isDrawerOpen, isSuccess, refreshKey],
    );
    const onOpenModalClick = (event: RoadmapEvent) => {
        if (!isMaturity(event)) {
            setEditedEvent(event);
            setTooltipContent(undefined);
        }
    };
    const onCloseModalClick = () => {
        setEditedEvent(undefined);
    };

    return (
        <>
            <ZfBox style={{ position: 'relative', display: 'flex', width: '100%', height: '100%' }}>
                {isLoading && <ZfLoadingSpinner style={{ position: 'absolute', top: '50%', left: '50%' }} />}
                {chart}
                {chart && yearEventMap && <CashEventPane yearEventMap={yearEventMap} />}
                {yearEntry && yearEntry.eventsAtCoordinate && (
                    <CashEventList
                        handleClose={handleCloseEventList}
                        events={yearEntry.eventsAtCoordinate}
                        openModal={onOpenModalClick}
                        tooltipContent={tooltipContent!}
                    />
                )}
            </ZfBox>
            {editedEvent && <NeedEventModal event={editedEvent} onClose={onCloseModalClick} isOpen={!!editedEvent} />}
        </>
    );
};
